<template>
  <div class="mc-module-container" :style="containerStyle">
    <!-- 网格布局 -->
    <div v-if="moduleType === 'grid'" class="grid-container">
      <div 
        v-for="(module, index) in modules" 
        :key="index"
        class="grid-module"
        :class="[`module-${module.shape || 'shape2'}`]"
      >
        <!-- 纯图片模式 -->
        <div 
          v-if="module.displayMode === 'image'"
          class="module-content image-mode"
          :style="{ backgroundImage: `url(${module.buttonImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }"
        >
        </div>
        <!-- 组合模式 -->
        <div 
          v-else
          class="module-content"
          :class="[module.shape || 'shape2']"
          :style="getModuleStyle(module)"
        >
          <img v-if="module.icon && module.icon.trim()" :src="module.icon" class="module-icon" />
          <div class="module-info">
            <div class="module-title">{{ module.title || '未命名模块' }}</div>
            <div v-if="module.subtitle" class="module-subtitle">{{ module.subtitle }}</div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 列表布局 -->
    <div v-else class="list-container">
      <div 
        v-for="(module, index) in modules" 
        :key="index"
        class="list-module"
      >
        <div 
          class="module-content"
          :class="[module.shape || 'shape2']"
          :style="getModuleStyle(module)"
        >
          <img v-if="module.icon && module.icon.trim()" :src="module.icon" class="module-icon" />
          <div class="module-info">
            <div class="module-title">{{ module.title || '未命名模块' }}</div>
            <div v-if="module.subtitle" class="module-subtitle">{{ module.subtitle }}</div>
          </div>
          <i class="el-icon-arrow-right module-arrow"></i>
        </div>
      </div>
    </div>
    
    <slot></slot>
  </div>
</template>

<script>
export default {
  name: 'McModuleContainer',
  props: {
    modules: {
      type: Array,
      default: () => []
    },
    moduleType: {
      type: String,
      default: 'grid'
    },
    styles: {
      type: Object,
      default: () => ({
        backgroundColor: '#F8F8F8',
        padding: 15,
        margin: 10,
        borderRadius: 8
      })
    }
  },
  computed: {
    containerStyle() {
      const { backgroundColor, padding, margin, borderRadius } = this.styles;
      return {
        backgroundColor: backgroundColor || '#F8F8F8',
        padding: `${padding || 15}px`,
        margin: `${margin || 10}px 0`,
        borderRadius: `${borderRadius || 8}px`,
        minHeight: '100px'
      };
    }
  },
  methods: {
    getModuleStyle(module) {
      return {
        backgroundColor: module.backgroundColor || '#409EFF',
        color: module.fontColor || '#FFFFFF'
      };
    }
  }
}
</script>

<style lang="scss" scoped>
.mc-module-container {
  width: 100%;
  box-sizing: border-box;
  
  .grid-container {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    grid-auto-rows: min-content;
    gap: 15px;
    width: 100%;
    
    .grid-module {
      width: 100%;
      
      /* 不同形状的模块尺寸 */
      &.module-shape1 {
        grid-column: span 3; /* 1.5格宽 = 3/6列 */
        grid-row: span 1;    /* 1格高 */
        height: 120px;       /* 统一高度 */
      }
      
      &.module-shape2 {
        grid-column: span 2; /* 1格宽 = 2/6列 */
        grid-row: span 1;    /* 1格高 */
        height: 120px;       /* 统一高度 */
      }
      
      &.module-shape3 {
        grid-column: span 3; /* 1.5格宽 = 3/6列 */
        grid-row: span 2;    /* 2格高 */
        height: 255px;       /* 2倍高度(240px) + 间隙(15px) */
      }
      
      &.module-shape4 {
        grid-column: span 6; /* 3格宽 = 6/6列 */
        grid-row: span 1;    /* 1格高 */
        height: 120px;       /* 统一高度 */
      }
      
      .module-content {
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 12px;
        border-radius: 8px;
        box-sizing: border-box;
        text-align: center;
        color: #fff;
        width: 100%;
        height: 100%;
        
        /* 所有形状统一使用相同的圆角 */
        &.shape1, &.shape2, &.shape3, &.shape4 {
          border-radius: 8px;
        }
        
        .module-icon {
          width: 36px;
          height: 36px;
          margin-bottom: 8px;
          object-fit: contain;
        }
        
        .module-info {
          width: 100%;
          overflow: hidden;
          
          .module-title {
            font-size: 14px;
            font-weight: bold;
            margin-bottom: 4px;
            line-height: 1.4;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
          }
          
          .module-subtitle {
            font-size: 12px;
            opacity: 0.8;
            line-height: 1.3;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 1;
            -webkit-box-orient: vertical;
          }
        }
        
        &.image-mode {
          width: 100%;
          height: 100%;
          min-height: 80px;
          border-radius: 8px;
          overflow: hidden;
          background-repeat: no-repeat;
        }
      }
    }
  }
  
  .list-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    
    .list-module {
      margin-bottom: 8px;
      
      &:last-child {
        margin-bottom: 0;
      }
      
      .module-content {
        position: relative;
        display: flex;
        align-items: center;
        padding: 10px 12px;
        border-radius: 8px;
        color: #fff;
        
        /* 不同形状的样式 */
        &.shape1 {
          border-radius: 8px;
        }
        
        &.shape2 {
          border-radius: 10px 0 0 10px;
        }
        
        &.shape3 {
          border-radius: 20px;
        }
        
        &.shape4 {
          border-radius: 0;
        }
        
        .module-icon {
          width: 24px;
          height: 24px;
          margin-right: 10px;
          flex-shrink: 0;
          object-fit: contain;
        }
        
        .module-info {
          flex: 1;
          overflow: hidden;
          min-width: 0;
          
          .module-title {
            font-size: 14px;
            font-weight: bold;
            line-height: 1.3;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          
          .module-subtitle {
            font-size: 12px;
            opacity: 0.8;
            margin-top: 2px;
            line-height: 1.3;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
        
        .module-arrow {
          margin-left: 8px;
          flex-shrink: 0;
        }
      }
    }
  }
}
</style> 